import { createContext, useContext, useState, useCallback } from 'react';

const LanguageContext = createContext(null);

const translations = {
  en: {
    // App
    appName: 'DukaanMate',
    appTagline: 'Inventory & Invoice Management',
    
    // Nav
    home: 'Home',
    signOut: 'Sign Out',
    
    // Home buttons
    newSale: 'New Sale',
    createInvoice: 'Create invoice',
    addStock: 'Stock',
    receiveInventory: 'Manage inventory',
    returnLabel: 'Return',
    processReturn: 'Process return',
    reports: 'Reports',
    viewAnalytics: 'View analytics',
    customers: 'Customers',
    manageCustomers: 'Manage customers',
    adminSettings: 'Admin Settings',
    configureApp: 'Configure app',
    products: 'Products',
    addChangeProduct: 'Product',
    
    // Stats
    todaySales: "Today's Sales",
    todayInvoices: "Today's Invoices",
    lowStockItems: 'Low Stock Items',
    totalProducts: 'Total Products',
    recentActivity: 'Recent Activity',
    noRecentActivity: 'No recent activity',
    lowStockAlerts: 'Low Stock Alerts',
    allStockHealthy: 'All stock levels healthy',
    left: 'left',
    
    // Login
    signIn: 'Sign In',
    emailAddress: 'Email Address',
    password: 'Password',
    dontHaveAccount: "Don't have an account?",
    register: 'Register',
    
    // Register
    createAccount: 'Create Account',
    fullName: 'Full Name',
    confirmPassword: 'Confirm Password',
    alreadyHaveAccount: 'Already have an account?',
    
    // New Sale
    cart: 'Cart',
    cartEmpty: 'Cart is empty',
    searchProducts: 'Search products by name, code, or barcode...',
    noProductsFound: 'No products found',
    startTyping: 'Start typing to search products',
    customerName: 'Customer name (optional)',
    payAndPrint: 'Pay & Print',
    total: 'TOTAL',
    saleComplete: 'Sale Complete!',
    printInvoice: 'Print Invoice',
    clear: 'Clear',
    cash: 'Cash',
    card: 'Card',
    upi: 'UPI',
    
    // Add Stock
    addStockTitle: 'Add Stock',
    receiveIntoStock: 'Receive inventory into stock',
    uploadStock: 'Upload Stock',
    bulkUploadCSV: 'Bulk upload products via CSV',
    adjustStock: 'Adjust Stock',
    correctLevels: 'Correct stock levels',
    product: 'Product',
    selectProduct: 'Select a product',
    quantity: 'Quantity',
    costPrice: 'Cost Price',
    taxPaid: 'Tax Paid (on Purchase)',
    taxPaidHint: 'Enter the tax amount paid when purchasing this inventory',
    taxReport: 'Tax Report',
    taxCollected: 'Tax Collected (Sales)',
    taxPaidTotal: 'Tax Paid (Purchases)',
    netTax: 'Net Tax Liability',
    taxOnSales: 'Tax on Sales',
    taxOnPurchases: 'Tax on Purchases',
    supplierName: 'Supplier Name',
    referenceNumber: 'Reference Number',
    notes: 'Notes',
    stockAdded: 'Stock Added!',
    addMore: 'Add More Stock',
    backToHome: 'Back to Home',
    currentStock: 'Current stock',
    save: 'Save',
    
    // Upload Stock
    downloadTemplate: 'Download CSV Template',
    uploadCSVFile: 'Upload CSV File',
    uploadFile: 'Upload File',
    uploadResults: 'Upload Results',
    itemsUploaded: 'items uploaded successfully',
    errors: 'errors',
    uploadAnother: 'Upload Another',
    
    // Adjust Stock
    adjustmentQuantity: 'Adjustment Quantity',
    enterPositiveNegative: 'Enter positive to add, negative to subtract',
    reason: 'Reason',
    selectReason: 'Select reason',
    damaged: 'Damaged',
    lost: 'Lost',
    foundStock: 'Found Stock',
    manualCorrection: 'Manual Correction',
    other: 'Other',
    stockAdjusted: 'Stock Adjusted!',
    newStock: 'New Stock',
    adjustMore: 'Adjust More',
    
    // Returns
    processReturnTitle: 'Process Return',
    returnItemsRestore: 'Return items and restore stock',
    originalInvoice: 'Original Invoice (Optional)',
    noInvoice: 'No Invoice',
    quantityReturned: 'Quantity',
    refundAmount: 'Refund Amount',
    returnReason: 'Return Reason',
    returnProcessed: 'Return Processed!',
    unitsReturned: 'units returned. Stock has been updated.',
    processAnother: 'Process Another Return',
    defective: 'Defective Product',
    wrongItem: 'Wrong Item',
    notNeeded: 'Not Needed',
    
    // Products
    manageProductCatalog: 'Manage your product catalog',
    addProduct: 'Add Product',
    searchProductsShort: 'Search products...',
    allCategories: 'All Categories',
    productCode: 'Product Code',
    sku: 'SKU',
    productName: 'Product Name',
    category: 'Category',
    brand: 'Brand',
    sellingPrice: 'Selling Price',
    tax: 'Tax',
    reorderLevel: 'Reorder Level',
    barcode: 'Barcode',
    cancel: 'Cancel',
    saveProduct: 'Save Product',
    editProduct: 'Edit Product',
    addNewProduct: 'Add New Product',
    uploadProductList: 'Upload Product List',
    downloadProductTemplate: 'Download Product Template',
    
    // Customers
    manageCustomerInfo: 'Manage customer information',
    addCustomer: 'Add Customer',
    searchCustomers: 'Search customers...',
    customerCode: 'Customer Code',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    editCustomer: 'Edit Customer',
    addNewCustomer: 'Add New Customer',
    saveCustomer: 'Save Customer',
    
    // Reports
    businessInsights: 'View business insights and analytics',
    fromDate: 'From Date',
    toDate: 'To Date',
    applyFilters: 'Apply Filters',
    stock: 'Stock',
    lowStock: 'Low Stock',
    sales: 'Sales',
    returns: 'Returns',
    profit: 'Profit',
    movement: 'Movement',
    salesHistory: 'Sales History',
    export: 'Export',
    currentStockReport: 'Current Stock Report',
    lowStockReport: 'Low Stock Report',
    salesReport: 'Sales Report',
    returnsReport: 'Returns Report',
    profitSummary: 'Profit Summary',
    stockMovement: 'Stock Movement Report',
    totalRevenue: 'Total Revenue',
    totalRefunds: 'Total Refunds',
    netRevenue: 'Net Revenue',
    invoicesReturns: 'Invoices / Returns',
    reprintInvoice: 'Reprint Invoice',
    dashboard: 'Dashboard',
    dashboardSubtitle: 'Business overview at a glance',
    chartsGraphs: 'Charts & graphs',
    viewPastInvoices: 'View past invoices',
    
    // Settings
    adminSettingsTitle: 'Admin Settings',
    configureStore: 'Configure your store and manage users',
    general: 'General',
    userAccess: 'User Access',
    storeName: 'Store Name',
    currency: 'Currency',
    defaultTax: 'Default Tax %',
    invoiceFooter: 'Invoice Footer Text',
    saveSettings: 'Save Settings',
    userAccessControl: 'User Access Control',
    addStaffUser: 'Add Staff User',
    noStaffUsers: 'No staff users yet',
    createUser: 'Create User',
    
    // Data Export
    dataExport: 'Data Export',
    exportAllData: 'Export All Data',
    exportAllDesc: 'Download all store data as CSV files',
    exportNow: 'Export Now',
    exporting: 'Exporting...',
    exportComplete: 'Export complete!',
    exportFailed: 'Export failed',
    autoExport: 'Auto Export Schedule',
    autoExportDesc: 'Automatically export data at a scheduled time',
    scheduleEnabled: 'Schedule Enabled',
    frequency: 'Frequency',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    exportTime: 'Export Time',
    dayOfWeek: 'Day of Week',
    dayOfMonth: 'Day of Month',
    saveFolder: 'Save Location',
    saveFolderDesc: 'Choose a folder to save exported files',
    chooseFolder: 'Choose Folder',
    folderSelected: 'Folder selected',
    noFolderSelected: 'Default: Browser Downloads folder',
    saveSchedule: 'Save Schedule',
    scheduleSaved: 'Schedule saved!',
    nextExport: 'Next Export',
    lastExport: 'Last Export',
    never: 'Never',
    
    // Common
    loading: 'Loading...',
    actions: 'Actions',
    status: 'Status',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    delete: 'Delete',
    edit: 'Edit',
    price: 'Price',
    date: 'Date',
    invoice: 'Invoice',
    items: 'Items',
    view: 'View',
    language: 'Language',
    english: 'English',
    hindi: 'Hindi',
    thankYou: 'Thank you for your business!',
  },
  hi: {
    // App
    appName: 'DukaanMate',
    appTagline: 'इन्वेंटरी और बिल प्रबंधन',
    
    // Nav
    home: 'होम',
    signOut: 'लॉग आउट',
    
    // Home buttons
    newSale: 'नई बिक्री',
    createInvoice: 'बिल बनाएं',
    addStock: 'स्टॉक',
    receiveInventory: 'इन्वेंटरी प्रबंधन',
    returnLabel: 'वापसी',
    processReturn: 'वापसी प्रक्रिया',
    reports: 'रिपोर्ट',
    viewAnalytics: 'विश्लेषण देखें',
    customers: 'ग्राहक',
    manageCustomers: 'ग्राहक प्रबंधन',
    adminSettings: 'एडमिन सेटिंग्स',
    configureApp: 'ऐप सेटिंग्स',
    products: 'उत्पाद',
    addChangeProduct: 'उत्पाद',
    
    // Stats
    todaySales: 'आज की बिक्री',
    todayInvoices: 'आज के बिल',
    lowStockItems: 'कम स्टॉक',
    totalProducts: 'कुल उत्पाद',
    recentActivity: 'हाल की गतिविधि',
    noRecentActivity: 'कोई हाल की गतिविधि नहीं',
    lowStockAlerts: 'कम स्टॉक अलर्ट',
    allStockHealthy: 'सभी स्टॉक स्तर सही हैं',
    left: 'बचे',
    
    // Login
    signIn: 'लॉगिन',
    emailAddress: 'ईमेल',
    password: 'पासवर्ड',
    dontHaveAccount: 'खाता नहीं है?',
    register: 'रजिस्टर करें',
    
    // Register
    createAccount: 'खाता बनाएं',
    fullName: 'पूरा नाम',
    confirmPassword: 'पासवर्ड पुष्टि',
    alreadyHaveAccount: 'पहले से खाता है?',
    
    // New Sale
    cart: 'कार्ट',
    cartEmpty: 'कार्ट खाली है',
    searchProducts: 'नाम, कोड या बारकोड से खोजें...',
    noProductsFound: 'कोई उत्पाद नहीं मिला',
    startTyping: 'खोजने के लिए टाइप करें',
    customerName: 'ग्राहक का नाम (वैकल्पिक)',
    payAndPrint: 'भुगतान और प्रिंट',
    total: 'कुल',
    saleComplete: 'बिक्री पूर्ण!',
    printInvoice: 'बिल प्रिंट करें',
    clear: 'साफ़ करें',
    cash: 'नकद',
    card: 'कार्ड',
    upi: 'UPI',
    
    // Add Stock
    addStockTitle: 'स्टॉक जोड़ें',
    receiveIntoStock: 'इन्वेंटरी में स्टॉक जोड़ें',
    uploadStock: 'स्टॉक अपलोड',
    bulkUploadCSV: 'CSV से बल्क अपलोड',
    adjustStock: 'स्टॉक एडजस्ट',
    correctLevels: 'स्टॉक स्तर सही करें',
    product: 'उत्पाद',
    selectProduct: 'उत्पाद चुनें',
    quantity: 'मात्रा',
    costPrice: 'लागत मूल्य',
    taxPaid: 'कर भुगतान (खरीद पर)',
    taxPaidHint: 'इस इन्वेंटरी खरीदते समय भुगतान किया गया कर राशि दर्ज करें',
    taxReport: 'कर रिपोर्ट',
    taxCollected: 'कर एकत्रित (बिक्री)',
    taxPaidTotal: 'कर भुगतान (खरीद)',
    netTax: 'शुद्ध कर देयता',
    taxOnSales: 'बिक्री पर कर',
    taxOnPurchases: 'खरीद पर कर',
    supplierName: 'आपूर्तिकर्ता का नाम',
    referenceNumber: 'संदर्भ संख्या',
    notes: 'नोट्स',
    stockAdded: 'स्टॉक जोड़ा गया!',
    addMore: 'और स्टॉक जोड़ें',
    backToHome: 'होम पर जाएं',
    currentStock: 'वर्तमान स्टॉक',
    save: 'सेव करें',
    
    // Upload Stock
    downloadTemplate: 'CSV टेम्पलेट डाउनलोड करें',
    uploadCSVFile: 'CSV फाइल अपलोड करें',
    uploadFile: 'फाइल अपलोड करें',
    uploadResults: 'अपलोड परिणाम',
    itemsUploaded: 'आइटम सफलतापूर्वक अपलोड हुए',
    errors: 'त्रुटियां',
    uploadAnother: 'और अपलोड करें',
    
    // Adjust Stock
    adjustmentQuantity: 'समायोजन मात्रा',
    enterPositiveNegative: 'जोड़ने के लिए +, घटाने के लिए - डालें',
    reason: 'कारण',
    selectReason: 'कारण चुनें',
    damaged: 'क्षतिग्रस्त',
    lost: 'खोया हुआ',
    foundStock: 'मिला हुआ स्टॉक',
    manualCorrection: 'मैनुअल सुधार',
    other: 'अन्य',
    stockAdjusted: 'स्टॉक एडजस्ट हुआ!',
    newStock: 'नया स्टॉक',
    adjustMore: 'और एडजस्ट करें',
    
    // Returns
    processReturnTitle: 'वापसी प्रक्रिया',
    returnItemsRestore: 'वापसी और स्टॉक अपडेट',
    originalInvoice: 'मूल बिल (वैकल्पिक)',
    noInvoice: 'कोई बिल नहीं',
    quantityReturned: 'मात्रा',
    refundAmount: 'रिफंड राशि',
    returnReason: 'वापसी का कारण',
    returnProcessed: 'वापसी प्रक्रिया पूर्ण!',
    unitsReturned: 'इकाइयां वापस। स्टॉक अपडेट हुआ।',
    processAnother: 'और वापसी प्रक्रिया',
    defective: 'खराब उत्पाद',
    wrongItem: 'गलत आइटम',
    notNeeded: 'जरूरत नहीं',
    
    // Products
    manageProductCatalog: 'उत्पाद सूची प्रबंधित करें',
    addProduct: 'उत्पाद जोड़ें',
    searchProductsShort: 'उत्पाद खोजें...',
    allCategories: 'सभी श्रेणियां',
    productCode: 'उत्पाद कोड',
    sku: 'SKU',
    productName: 'उत्पाद का नाम',
    category: 'श्रेणी',
    brand: 'ब्रांड',
    sellingPrice: 'बिक्री मूल्य',
    tax: 'कर',
    reorderLevel: 'री-ऑर्डर स्तर',
    barcode: 'बारकोड',
    cancel: 'रद्द करें',
    saveProduct: 'उत्पाद सेव करें',
    editProduct: 'उत्पाद संपादित करें',
    addNewProduct: 'नया उत्पाद जोड़ें',
    uploadProductList: 'उत्पाद सूची अपलोड',
    downloadProductTemplate: 'उत्पाद टेम्पलेट डाउनलोड',
    
    // Customers
    manageCustomerInfo: 'ग्राहक जानकारी प्रबंधित करें',
    addCustomer: 'ग्राहक जोड़ें',
    searchCustomers: 'ग्राहक खोजें...',
    customerCode: 'ग्राहक कोड',
    name: 'नाम',
    phone: 'फ़ोन',
    email: 'ईमेल',
    address: 'पता',
    editCustomer: 'ग्राहक संपादित करें',
    addNewCustomer: 'नया ग्राहक जोड़ें',
    saveCustomer: 'ग्राहक सेव करें',
    
    // Reports
    businessInsights: 'व्यापार विश्लेषण और रिपोर्ट',
    fromDate: 'से तारीख',
    toDate: 'तक तारीख',
    applyFilters: 'फ़िल्टर लगाएं',
    stock: 'स्टॉक',
    lowStock: 'कम स्टॉक',
    sales: 'बिक्री',
    returns: 'वापसी',
    profit: 'लाभ',
    movement: 'मूवमेंट',
    salesHistory: 'बिक्री इतिहास',
    export: 'एक्सपोर्ट',
    currentStockReport: 'वर्तमान स्टॉक रिपोर्ट',
    lowStockReport: 'कम स्टॉक रिपोर्ट',
    salesReport: 'बिक्री रिपोर्ट',
    returnsReport: 'वापसी रिपोर्ट',
    profitSummary: 'लाभ सारांश',
    stockMovement: 'स्टॉक मूवमेंट रिपोर्ट',
    totalRevenue: 'कुल राजस्व',
    totalRefunds: 'कुल रिफंड',
    netRevenue: 'शुद्ध राजस्व',
    invoicesReturns: 'बिल / वापसी',
    reprintInvoice: 'बिल दोबारा प्रिंट',
    dashboard: 'डैशबोर्ड',
    dashboardSubtitle: 'व्यापार की स्थिति एक नज़र में',
    chartsGraphs: 'चार्ट और ग्राफ',
    viewPastInvoices: 'पिछले बिल देखें',
    
    // Settings
    adminSettingsTitle: 'एडमिन सेटिंग्स',
    configureStore: 'दुकान सेटिंग्स और यूजर प्रबंधन',
    general: 'सामान्य',
    userAccess: 'यूजर एक्सेस',
    storeName: 'दुकान का नाम',
    currency: 'मुद्रा',
    defaultTax: 'डिफ़ॉल्ट कर %',
    invoiceFooter: 'बिल फुटर टेक्स्ट',
    saveSettings: 'सेटिंग्स सेव करें',
    userAccessControl: 'यूजर एक्सेस कंट्रोल',
    addStaffUser: 'स्टाफ यूजर जोड़ें',
    noStaffUsers: 'कोई स्टाफ यूजर नहीं',
    createUser: 'यूजर बनाएं',
    
    // Data Export
    dataExport: 'डेटा एक्सपोर्ट',
    exportAllData: 'सभी डेटा एक्सपोर्ट',
    exportAllDesc: 'सभी दुकान डेटा CSV फाइलों में डाउनलोड करें',
    exportNow: 'अभी एक्सपोर्ट करें',
    exporting: 'एक्सपोर्ट हो रहा है...',
    exportComplete: 'एक्सपोर्ट पूरा!',
    exportFailed: 'एक्सपोर्ट विफल',
    autoExport: 'ऑटो एक्सपोर्ट शेड्यूल',
    autoExportDesc: 'शेड्यूल किए गए समय पर ऑटोमैटिक डेटा एक्सपोर्ट',
    scheduleEnabled: 'शेड्यूल चालू',
    frequency: 'आवृत्ति',
    daily: 'रोजाना',
    weekly: 'साप्ताहिक',
    monthly: 'मासिक',
    exportTime: 'एक्सपोर्ट समय',
    dayOfWeek: 'हफ्ते का दिन',
    dayOfMonth: 'महीने का दिन',
    saveFolder: 'सेव लोकेशन',
    saveFolderDesc: 'एक्सपोर्ट फाइलें सेव करने के लिए फोल्डर चुनें',
    chooseFolder: 'फोल्डर चुनें',
    folderSelected: 'फोल्डर चुना गया',
    noFolderSelected: 'डिफ़ॉल्ट: ब्राउज़र डाउनलोड फोल्डर',
    saveSchedule: 'शेड्यूल सेव करें',
    scheduleSaved: 'शेड्यूल सेव हो गया!',
    nextExport: 'अगला एक्सपोर्ट',
    lastExport: 'आखिरी एक्सपोर्ट',
    never: 'कभी नहीं',
    
    // Common
    loading: 'लोड हो रहा है...',
    actions: 'कार्रवाई',
    status: 'स्थिति',
    inStock: 'स्टॉक में',
    outOfStock: 'स्टॉक में नहीं',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    price: 'मूल्य',
    date: 'तारीख',
    invoice: 'बिल',
    items: 'आइटम',
    view: 'देखें',
    language: 'भाषा',
    english: 'English',
    hindi: 'हिन्दी',
    thankYou: 'आपके व्यापार के लिए धन्यवाद!',
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('dm_language') || 'en';
  });

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    const newLang = language === 'en' ? 'hi' : 'en';
    setLanguage(newLang);
    localStorage.setItem('dm_language', newLang);
  }, [language]);

  const setLang = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('dm_language', lang);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, t, toggleLanguage, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
